import type { CapRouterOutlet } from '@capgo/capacitor-transitions';

import { NativeNavigationHistory, nativeParentRoute } from './native-navigation-history';

const HISTORY_KEY = '__zameelNativeNavigation';

function historyRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? { ...value } : {};
}

function currentHref() {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function createNativeNavigationRuntime() {
  const history = new NativeNavigationHistory();
  const listeners = new Set<() => void>();
  let outlet: CapRouterOutlet | null = null;
  let page: HTMLElement | null = null;
  let pageKey: string | undefined;
  const scopes = new Map<'tenant' | 'user', string>();
  let sequence = 0;
  // getRandomValues also works over HTTP when testing on a phone via LAN.
  const session = Array.from(crypto.getRandomValues(new Uint8Array(16)),
    (byte) => byte.toString(16).padStart(2, '0')).join('');
  const nextKey = () => `${session}:${++sequence}`;
  let key = nextKey();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const originalPush = window.history.pushState;
  const originalReplace = window.history.replaceState;
  let frame = 0;
  let destroyed = false;
  let notificationPending = false;

  function notifyListeners() {
    if (notificationPending) return;
    notificationPending = true;
    // Next writes history during useInsertionEffect; React updates must wait.
    queueMicrotask(() => {
      notificationPending = false;
      if (!destroyed) listeners.forEach((listener) => listener());
    });
  }

  function hasOverlay() {
    return Array.from(document.querySelectorAll<HTMLElement>(
      '[role="dialog"][data-state="open"], [role="alertdialog"], [aria-modal="true"], [data-native-menu-open="true"]',
    )).some((element) => {
      if (element.closest('[inert], [aria-hidden="true"]')) return false;

      return element.getClientRects().length > 0 &&
        window.getComputedStyle(element).visibility !== 'hidden';
    });
  }

  function updateGesture() {
    outlet?.setSwipeGesture(history.canGoBack && !reducedMotion.matches && !hasOverlay() ? 'auto' : false);
  }

  function setIntent() {
    outlet?.setNavigation(history.action, reducedMotion.matches || history.action === 'root' ? 'none' : history.action);
    updateGesture();
  }

  function visit(operation: 'push' | 'replace' | 'pop', next: string) {
    const samePage = history.current && new URL(history.current.href, location.origin).pathname === location.pathname;
    const scrollTop = page?.scrollTop ?? 0;
    key = next;
    history.visit(key, currentHref(), operation, false);
    if (samePage && page && history.current) {
      pageKey = key;
      history.current.scrollTop = scrollTop;
      if (history.action === 'root' && outlet) {
        // Query-only pushes do not remount React's page, but invalidate the old swipe stack.
        const controller = outlet.getController();
        for (const entry of [...controller.stack]) {
          if (entry.element !== page) controller.removePage(entry.id);
        }
        scheduleCleanup();
      }
    }
    setIntent();
    notifyListeners();
  }

  const push: History['pushState'] = (data: unknown, unused, url) => {
    if (destroyed) return originalPush.call(window.history, data, unused, url);
    const next = nextKey();
    originalPush.call(window.history, { ...historyRecord(data), [HISTORY_KEY]: next }, unused, url);
    visit('push', next);
  };
  const replace: History['replaceState'] = (data: unknown, unused, url) => {
    if (destroyed) return originalReplace.call(window.history, data, unused, url);
    const incomingKey = historyRecord(data)[HISTORY_KEY];
    // Next may restore a popped entry before our popstate listener runs.
    const restoredEntry = history.entries.find((entry) =>
      entry.key === incomingKey && entry.key !== key && entry.href === currentHref());
    if (restoredEntry) {
      originalReplace.call(window.history, data, unused, url);
      visit('pop', restoredEntry.key);
      return;
    }
    originalReplace.call(window.history, { ...historyRecord(data), [HISTORY_KEY]: key }, unused, url);
    if (history.current?.href !== currentHref()) visit('replace', key);
  };

  function onPop(event: PopStateEvent) {
    const value = historyRecord(event.state)[HISTORY_KEY];
    if (value === history.current?.key && history.current?.href === currentHref()) return;
    visit('pop', typeof value === 'string' && value.startsWith(`${session}:`) ? value : nextKey());
  }

  function onClick(event: MouseEvent) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (outlet?.getController().animating) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const anchor = event.target instanceof Element ? event.target.closest('a') : null;
    const targetPath = anchor ? new URL(anchor.href, location.origin).pathname : null;
    if (anchor?.dataset.nativeNavigation === 'tab' && targetPath) {
      const index = history.entries.findLastIndex((entry) =>
        new URL(entry.href, location.origin).pathname === targetPath);
      if (index >= 0) {
        event.preventDefault();
        event.stopPropagation();
        const distance = index - history.entries.length + 1;
        if (distance !== 0) window.history.go(distance);
        return;
      }
    }
  }

  function cleanSnapshots() {
    frame = 0;
    if (!outlet) return;
    const controller = outlet.getController();
    if (controller.animating) {
      frame = requestAnimationFrame(cleanSnapshots);
      return;
    }
    const retained = new Set(controller.stack.map((entry) => entry.element));
    for (const child of Array.from(outlet.children)) {
      if (child instanceof HTMLElement && child !== page && !retained.has(child)) child.remove();
    }
    updateGesture();
  }

  function scheduleCleanup() {
    if (!frame) frame = requestAnimationFrame(cleanSnapshots);
  }

  // The outlet owns discarded DOM snapshots, never extra mounted React trees.
  const observer = new MutationObserver(scheduleCleanup);
  const guardGesture = () => updateGesture();
  history.visit(key, currentHref(), 'replace', true);
  originalReplace.call(window.history, { ...historyRecord(window.history.state), [HISTORY_KEY]: key }, '');
  window.history.pushState = push;
  window.history.replaceState = replace;
  window.addEventListener('popstate', onPop);
  document.addEventListener('click', onClick, true);
  document.addEventListener('pointerdown', guardGesture, true);
  reducedMotion.addEventListener('change', updateGesture);

  return {
    history,
    resetScope(nextScope: string, dimension: 'tenant' | 'user' = 'tenant') {
      const scope = scopes.get(dimension);
      scopes.set(dimension, nextScope);
      if (scope === undefined || scope === nextScope) return;
      key = nextKey();
      history.visit(key, currentHref(), 'push', true);
      originalReplace.call(window.history, { ...historyRecord(window.history.state), [HISTORY_KEY]: key }, '');
      pageKey = key;
      if (outlet) {
        const controller = outlet.getController();
        for (const entry of [...controller.stack]) {
          if (entry.element !== page) controller.removePage(entry.id);
        }
      }
      setIntent();
      notifyListeners();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    attachOutlet(element: CapRouterOutlet) {
      outlet = element;
      observer.observe(element, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
      updateGesture();
    },
    attachPage(element: HTMLElement) {
      page = element;
      pageKey = history.current?.key;
      element.inert = false;
      element.removeAttribute('aria-hidden');
      scheduleCleanup();
    },
    saveScroll(element: HTMLElement) {
      if (history.current && element === page && history.current.key === pageKey) history.current.scrollTop = element.scrollTop;
    },
    detachPage(element: HTMLElement) {
      // React has unmounted this tree. Capgo may retain its DOM to reveal on swipe.
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
      queueMicrotask(() => {
        if (element.inert) {
          for (const node of element.querySelectorAll('[id]')) node.removeAttribute('id');
        }
      });
      if (page === element) page = null;
    },
    back(navigate: (href: string) => void) {
      if (outlet?.getController().animating) return;
      if (history.canGoBack) window.history.back();
      else navigate(nativeParentRoute(location.pathname));
    },
    destroy() {
      destroyed = true;
      listeners.clear();
      observer.disconnect();
      cancelAnimationFrame(frame);
      if (window.history.pushState === push) window.history.pushState = originalPush;
      if (window.history.replaceState === replace) window.history.replaceState = originalReplace;
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('pointerdown', guardGesture, true);
      reducedMotion.removeEventListener('change', updateGesture);
      outlet = null;
      page = null;
    },
  };
}

export type NativeNavigationRuntime = ReturnType<typeof createNativeNavigationRuntime>;

/** Data-backed screens may grow after their route commits. Stop when reached or touched. */
export function restoreNativeScroll(element: HTMLElement, top: number, hash: string) {
  let done = false;
  const stop = () => {
    done = true;
    observer.disconnect();
    clearTimeout(timeout);
    element.removeEventListener('pointerdown', stop);
    element.removeEventListener('wheel', stop);
  };
  const restore = () => {
    if (done) return;
    if (hash) {
      let id: string;
      try { id = decodeURIComponent(hash.slice(1)); } catch { stop(); return; }
      const target = Array.from(element.querySelectorAll('[id]')).find((node) => node.id === id);
      if (target) { target.scrollIntoView(); stop(); }
      return;
    }
    element.scrollTop = top;
    if (Math.abs(element.scrollTop - top) < 2) stop();
  };
  const observer = new ResizeObserver(restore);
  const timeout = setTimeout(stop, 8000);
  for (const child of Array.from(element.children)) observer.observe(child);
  element.addEventListener('pointerdown', stop, { once: true });
  element.addEventListener('wheel', stop, { once: true });
  restore();
  return stop;
}
